/**
 * A protocol's live tickets for promotion: the graded pool and its types, one type's tickets, sorting the
 * open tickets into types, and a person's Correct or Incorrect mark on them.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type { BucketRun } from '@/types/websocket.types';
import type {
	LiveDecision,
	LivePoolView,
	LivePrepareView,
	LiveTicket,
	LiveTicketReview,
	ReviewVerdict
} from '@/modules/promotion-v2/types';

export const livePromotionService = {
	async getLivePool(sopId: string): Promise<LivePoolView> {
		const response = await api.get<ApiResponse<LivePoolView>>(API_ENDPOINTS.PROMOTION.LIVE(sopId));
		return response.data.data;
	},

	async getTypeTickets(sopId: string, typeId: string): Promise<LiveTicket[]> {
		const response = await api.get<ApiResponse<{ tickets: LiveTicket[] }>>(
			API_ENDPOINTS.PROMOTION.LIVE_TYPE(sopId, typeId)
		);
		return response.data.data.tickets;
	},

	/** Starts Promote's preparation; a step already running is reported, not restarted. */
	async prepare(sopId: string): Promise<LivePrepareView> {
		const response = await api.post<ApiResponse<LivePrepareView>>(API_ENDPOINTS.PROMOTION.LIVE_PREPARE(sopId));
		return response.data.data;
	},

	async getDecisions(sopId: string, typeId: string, tool: string): Promise<LiveDecision[]> {
		const response = await api.get<ApiResponse<{ decisions: LiveDecision[] }>>(
			API_ENDPOINTS.PROMOTION.LIVE_DECISIONS(sopId, typeId, tool)
		);
		return response.data.data.decisions;
	},

	/** Sorts the open live tickets into the protocol's types; a pass in progress is reported, not restarted. */
	async startBucketing(sopId: string): Promise<BucketRun> {
		const response = await api.post<ApiResponse<{ run: BucketRun }>>(API_ENDPOINTS.PROMOTION.LIVE_BUCKET(sopId));
		return response.data.data.run;
	},

	/** Marks tickets Correct or Incorrect; null clears the mark. */
	async review(
		sopId: string,
		scenarioIds: string[],
		verdict: ReviewVerdict | null
	): Promise<Array<{ scenarioId: string; review: LiveTicketReview | null }>> {
		const response = await api.post<
			ApiResponse<{ reviews: Array<{ scenarioId: string; review: LiveTicketReview | null }> }>
		>(API_ENDPOINTS.PROMOTION.LIVE_REVIEW(sopId), { scenarioIds, verdict });
		return response.data.data.reviews;
	}
};
